import React, {
	useEffect,
	useState,
	useMemo,
	useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Icon, { COLORS, ICONS } from '../../../general-components/icon';
import * as ModalActions from '../../../../stores/actions/modal';
import * as ShotsActions from '../../../../stores/actions/shots';
import { GreenroomState as GS } from '../../../../stores/types/types';
import Socket from '../../../../connection/socket';
import useShots from '../../../../contexts/shots/provider';
import type { Shot } from '../../../../stores/types/shots';
import LayoutSelector from '../../../general-components/layout-selector';
import {
	fixedLayoutTypes,
	LAYOUTS_BY_SIZE,
	VideoLayouts,
} from '../../../../utils/video-layout';
import SlidingMenu, { SlidingMenuOption } from '../../../general-components/sliding-menu';
import SelectGroup, { SelectGroupOption } from '../../../general-components/select-group';
import type { GraphicInterface } from '../../../../stores/types/graphics';
import {
	compareGraphic,
} from '../../../../utils/custom-graphics';
import { setCurrentGraphicsId } from '../../../../stores/actions/graphics';
import { bulkEditShots } from '../../../../stores/actions/shots';
import changeShotLayout from '../../../../utils/changeShotLayout';
import { validateShotData } from '../../../../connection/utils';
import { useAutoplayOnScheduledStream } from '../../../../hooks/use-shot-utilities';
import useCloseEditModal, { useShotEditModalIsLocked } from '../../../../hooks/use-close-edit-modal';
import ShotEditUI from './shot-edit-ui';
import TooltipWrapper from '../../../general-components/tooltips/tooltip-wrapper';
import {
	ContentTemplate,
	StylePackData,
	StylePackGroup,
} from '../../../types/stylepacks';
import { META_MENU_STATES } from '../../../../stores/types/tabs';
import { templateOrder } from '../../../style-packs/style-packs-tab';
import { isEqual } from 'underscore';
import { IconStylepacksDuotoneCopy, IconTakesHighlightsDuotone } from '@brandlive1941/pixel';

export const SHOT_MODAL_PAGES = {
	LAYOUT: 'layout',
	EDITING: 'editing',
	SHOT_DELETED: 'shot_deleted',
};

const presenterOptions: SelectGroupOption[] = [
	{ label: '1-8', value: -1 },
	{ label: '1', value: 1 },
	{ label: '2', value: 2 },
	{ label: '3', value: 3 },
	{ label: '4', value: 4 },
	{ label: '5', value: 5 },
	{ label: '6', value: 6 },
	{ label: '7', value: 7 },
	{ label: '8', value: 8 },
];

const tabOptions: SlidingMenuOption[] = [
	{ label: 'All' },
	{ label: 'Presenters' },
	{ label: 'Media' },
];

export interface ShotModalStylepackDrawer {
	state: string;
	showInfo: boolean;
	stylePack: StylePackData | null;
	stylePackItem: string;
}

interface Props {
	socket: Socket | null;
	modalPage?: string | undefined;
	modalTab?: any;
	modalGraphicsLoading?: boolean;
}

// see if the css class includes keywords 'media' or 'fixed'
const mediaFixedLayouts = (s: string): boolean => fixedLayoutTypes.some((f) => s?.includes(f));
const presenterLayouts = (s: string): boolean => !fixedLayoutTypes.some((f) => s?.includes(f));

export default function ShotBuilder(props: Props) {
	const dispatch = useDispatch();
	const {
		socket,
		modalPage,
		modalTab,
		modalGraphicsLoading,
	} = props;

	const token = useSelector((s: GS) => s.SessionReducer.token);
	const session = useSelector((s: GS) => s.SessionReducer.session);
	const meeting = useSelector((s: GS) => s.SessionReducer.meeting);
	const user = useSelector((s: GS) => s.SessionReducer.user);
	const shotModalShot = useSelector((s: GS) => s.ShotsReducer.shotModalShot);
	const shots = useSelector((s: GS) => s.ShotsReducer.shots);
	const currentShotId = useSelector((s: GS) => s.ShotsReducer.currentShotId);
	const shotIdToDelete = useSelector((s: GS) => s.ShotsReducer.shotIdToDelete);
	const graphics = useSelector((s: GS) => s.GraphicsReducer.graphics);
	const shotModalGraphics = useSelector((s: GS) => s.GraphicsReducer.shotModalGraphics);
	const media = useSelector((s: GS) => s.MediaReducer.media);
	const modalData = useSelector((s: GS) => s.ModalReducer.modalData);
	const contentTemplates = useSelector((s: GS) => s.GeneratedContentReducer.contentTemplates);
	const useStylepacks = useSelector((s: GS) => s.SettingsReducer.featureFlags?.use_title_library);
	const upsertingContent = useSelector((s: GS) => s.GeneratedContentReducer.upsertingContent);
	const useNewShotThumbs = useSelector((s: GS) => s.SettingsReducer.featureFlags.use_new_shot_thumbs);

	const {
		handleAddingShot,
		editShotModalShot,
	} = useShots();
	const handleAutoplayOnScheduledStream = useAutoplayOnScheduledStream();
	const closeEditModal = useCloseEditModal();
	const isLocked = useShotEditModalIsLocked();

	const modalRef = useRef<any>(null);

	const [currentTab, setCurrentTab] = useState(tabOptions[0]);
	const [numberOfPresenters, setNumberOfPresenters] = useState(presenterOptions[0].value);
	const [selectedLayout, setSelectedLayout] = useState<string>(shotModalShot?.shot_data?.forcedLayout ?? '');
	const [shotModalStylepackDrawer, setShotModalStylepackDrawer] = useState<ShotModalStylepackDrawer>({
		state: META_MENU_STATES.CLOSED,
		showInfo: false,
		stylePack: null,
		stylePackItem: '',
	});

	const layoutThumbnailSize = 190 + 16; // width of layout selector buttons + right margin

	useEffect(() => {
		if (shotIdToDelete === shotModalShot.shot_id) {
			dispatch(ModalActions.setModalData({
				...modalData,
				modalPage: SHOT_MODAL_PAGES.SHOT_DELETED,
			}));
		}
	}, [shotIdToDelete, shotModalShot]);

	useEffect(() => {
		if (modalGraphicsLoading && shotModalGraphics.length > 0) {
			dispatch(ModalActions.setModalData({
				...modalData,
				modalGraphicsLoading: false,
			}));
		}
	}, [modalGraphicsLoading, shotModalGraphics]);

	useEffect(() => {
		socket?.sendMessage('request-modal-shot', { from: session, shot: shotModalShot });
		return () => { dispatch(setCurrentGraphicsId(0)); };
	}, []);

	const shotModalMedia = useMemo(() => {
		const mediaIds = shotModalShot.shot_data.shotFeeds.filter((feed) => feed.media).map((feed) => feed.media);
		return media.filter((mediaItem) => mediaIds.includes(mediaItem.id));
	}, [media, shotModalShot]);

	const stylepackUsed: { stylePack: string; name: string } | null = useMemo(() => {
		if (modalGraphicsLoading) return null;

		const templateTypes = [...shotModalGraphics, ...shotModalMedia]
			.filter(item => item.provider === 'capsule')
			.map(item => item.template_type);

		const stylepacks = templateTypes
			.map(type => contentTemplates.find(t => t.type === type))
			.filter((t): t is ContentTemplate => !!t)
			.map(t => ({ stylePack: t.stylePack, name: t.name }));

		const uniqueStylepacks = Array.from(
			new Map(stylepacks.map(sp => [sp.stylePack, sp])).values()
		);

		return uniqueStylepacks.length === 1 ? uniqueStylepacks[0] : null;
	}, [modalGraphicsLoading, shotModalGraphics, shotModalMedia, contentTemplates]);

	const cleanUpGlobalGraphicChanges = (updatedGraphics: GraphicInterface[]) => {
		if (!token) return;

		const graphicsNoLongGlobal = updatedGraphics.filter((updatedGraphic) => {
			const oldGraphic = graphics.find((g) => g.id === updatedGraphic.id);
			return (
				oldGraphic
				&& oldGraphic.is_global !== updatedGraphic?.is_global
				&& !updatedGraphic.is_global
			);
		}).map((g) => g.id);

		if (graphicsNoLongGlobal.length > 0) {
			const shotsContainingRemovedGlobalGraphics = shots.filter((s) => s?.shot_data?.shotGraphics
				?.some((g) => graphicsNoLongGlobal.indexOf(g) >= -1));

			if (shotsContainingRemovedGlobalGraphics.length > 0) {
				const editedShots = shotsContainingRemovedGlobalGraphics.map((originalShot) => {
					const editedShot = { ...originalShot };
					editedShot.shot_data.shotGraphics = editedShot.shot_data.shotGraphics
						?.filter((graphicId: number) => graphicsNoLongGlobal.indexOf(graphicId) < 0);
					return editedShot;
				});

				if (editedShots.length > 0) {
					dispatch(bulkEditShots(token, editedShots));
				}
			}
		}
	};

	const onDiscard = () => {
		closeEditModal(socket, false);
	};

	const handleSave = async (e?: React.MouseEvent<HTMLButtonElement>) => {
		if (!token || !user || !meeting) return;

		e?.currentTarget.blur();

		const updatedOrNewShot = { ...shotModalShot };

		updatedOrNewShot.edited_by = user.gr_user;
		updatedOrNewShot.meeting = meeting.meeting;

		const updatedOrNewShotAutoplay = handleAutoplayOnScheduledStream(
			updatedOrNewShot,
		);

		if (updatedOrNewShotAutoplay.shot_id) {
			// editing existing shot
			dispatch(ShotsActions.editShot(token, updatedOrNewShotAutoplay, meeting?.meeting_id, meeting?.channel, useNewShotThumbs));
			dispatch(ShotsActions.setShotTabShotId(updatedOrNewShotAutoplay.shot_id));
		} else {
			// creating new shot
			const currentShot = shots.find((s) => s.shot_id === currentShotId);
			updatedOrNewShotAutoplay.shot_data.order = currentShot?.shot_data
				? currentShot.shot_data.order + 1
				: shots.length;
			const addedShot = await handleAddingShot(updatedOrNewShotAutoplay);

			if (addedShot) {
				dispatch(ShotsActions.setShotTabShotId(addedShot?.shot_id));
			}
		}

		const updatedGraphics = shotModalGraphics.filter(
			(updatedGraphic: GraphicInterface) => compareGraphic(graphics, updatedGraphic),
		);

		if (updatedGraphics.length > 0) {
			// todo - send message to beehive to write temp data to API
			cleanUpGlobalGraphicChanges(updatedGraphics);
		}

		closeEditModal(socket, true);
	};

	const handleDrawerStateChange = (newDrawerState: ShotModalStylepackDrawer) => {
		// close the drawer if the state is the same
		if (isEqual(shotModalStylepackDrawer, newDrawerState)) {
			setShotModalStylepackDrawer({
				...shotModalStylepackDrawer,
				state: META_MENU_STATES.CLOSING,
			});
		} else {
			setShotModalStylepackDrawer(newDrawerState);
		}
	};

	const goToEditPage = () => {
		dispatch(ModalActions.setModalData({
			...modalData,
			modalPage: SHOT_MODAL_PAGES.EDITING,
		}));
		modalRef.current.focus();
	};

	const handleShotModalEnter = (keyDownEvent: React.KeyboardEvent<HTMLDivElement>) => {
		if (keyDownEvent.key === 'Enter') {
			if (['SELECT', 'INPUT'].includes(document.activeElement?.tagName ?? '')) {
				return;
			}

			if (modalPage === SHOT_MODAL_PAGES.LAYOUT) {
				goToEditPage();
			} else if (modalPage === SHOT_MODAL_PAGES.EDITING) {
				handleSave();
			}
		}
	};

	function scrollRow(index: number, right: boolean) {
		document.getElementById(`row-${index}`)?.scrollBy({
			top: 0,
			left: right ? layoutThumbnailSize : -layoutThumbnailSize,
			behavior: 'smooth',
		});
	}

	async function setShotLayout(layout: string, videoCount: number) {
		const shot: Shot = {
			...shotModalShot,
			shot_data: {
				...shotModalShot.shot_data,
			},
		};

		setSelectedLayout(layout);

		const editedShot = changeShotLayout(shot, layout, videoCount);
		if (editedShot?.shot_data) {
			editedShot.shot_data = await validateShotData(editedShot.shot_data, media);
			editShotModalShot(editedShot, socket);
		}
	}

	const filteredLayoutRows = useMemo(() => {
		let rows = [...LAYOUTS_BY_SIZE];

		// if they have all selected, 'number of presenters' will be -1
		if (numberOfPresenters > 0) {
			rows = rows.filter((l: VideoLayouts) => l.presenters === numberOfPresenters);
		}

		if (currentTab.label !== 'All') {
			rows = rows.map((layoutRow: VideoLayouts) => {
				const nLayouts = currentTab.label === 'Presenters'
					? layoutRow.layouts.filter(presenterLayouts)
					: layoutRow.layouts.filter(mediaFixedLayouts);

				return { ...layoutRow, layouts: nLayouts };
			});
		}

		return rows.filter((layoutRow: VideoLayouts) => layoutRow.layouts.length > 0);
	}, [currentTab, numberOfPresenters]);

	const isNewShot = useMemo(
		(): boolean => shotModalShot.shot_id === 0,
		[shotModalShot],
	);

	const addUpdateButtonText = useMemo((): string => {
		if (isNewShot) { return 'Add Shot'; }
		if (currentShotId === shotModalShot.shot_id) { return 'Update Live Shot'; }
		return 'Save Shot';
	}, [currentShotId, isNewShot, shotModalShot, isLocked]);

	return (!shotModalShot
		? <></>
		: <div
			className="info-modal shot-builder-modal"
			onKeyDown={(keyDownEvent) => handleShotModalEnter(keyDownEvent)}
			tabIndex={-1}
			ref={modalRef}
		>
			{modalPage === SHOT_MODAL_PAGES.LAYOUT && <>
				<div className="info-modal-header shot-builder-modal-layouts-page align-items-end justify-content-end shot-builder-modal-header flex-column">
					<h5 className="bl-font-header margin-none padding-left-large">
						{isNewShot ? 'Pick New Shot Layout' : 'Edit Shot Layout'}
					</h5>
					<div className="align-items-end justify-content-space-between padding-left-medium padding-right-medium">
						<SlidingMenu
							options={tabOptions}
							selectedOption={currentTab}
							callback={(newTab: any) => setCurrentTab(newTab)}
						/>

						<SelectGroup
							onChange={setNumberOfPresenters}
							options={presenterOptions}
							label={'Amount of presenters'}
							selected={
								presenterOptions.find(
									(option) => option.value === numberOfPresenters,
								) || presenterOptions[0]
							}
							placeholder={'Choose your Speaker'}
						/>
					</div>
				</div>
				<div className="flex-column shot-builder-modal-body shot-builder-modal-layouts-page">
					{filteredLayoutRows.length === 0 && <div className="width-full flex justify-content-center">Currently there are no supported layouts for this configuration.</div>}
					{filteredLayoutRows.map((videoLayout: VideoLayouts, rowIndex: number) => <div className="layout-row-container" key={rowIndex}>
							<label className="padding-left-large">{videoLayout.presenters}</label>
							<div className="layout-row shot-builder-layout-row padding-left-large" id={`row-${rowIndex}`}>
								{videoLayout.layouts.map((layout: string, layoutIndex: number) => (
									<LayoutSelector
										key={layoutIndex}
										onClick={() => {
											setShotLayout(layout, videoLayout.presenters);
											goToEditPage();
										}}
										onKeyDown={() => { }}
										selected={selectedLayout === layout}
										type={layout}
										number={videoLayout.presenters}
										layoutIndex={layoutIndex}
										displayIcon={false}
										hasPictureInPicture={layout?.includes('picture-in-picture')}
									/>
								))}
								<button className="scroll-arrow left" onMouseDown={() => scrollRow(rowIndex, false)}>
									<Icon name={ICONS.KEYBOARD_ARROW_LEFT} size={10} color={COLORS.BLACK} />
								</button>
								<button className="scroll-arrow" onMouseDown={() => scrollRow(rowIndex, true)}>
									<Icon name={ICONS.KEYBOARD_ARROW_RIGHT} size={10} color={COLORS.BLACK} />
								</button>
							</div>
						</div>)}
				</div>
				<div className="align-items-center justify-content-right shot-builder-modal-footer" />
			</>}

			{modalPage === SHOT_MODAL_PAGES.EDITING && <>
				<ShotEditUI
					socket={socket}
					modalTab={modalTab}
					shotModalStylepackDrawer={shotModalStylepackDrawer}
					setShotModalStylepackDrawer={setShotModalStylepackDrawer}
				/>
				<div className="align-items-center justify-content-space-between shot-builder-modal-footer">
					<div className="shot-builder-modal-footer-left-buttons">
						{useStylepacks && (
							<>
								<button
									onClick={() => {
										handleDrawerStateChange({
											state: shotModalStylepackDrawer.state === META_MENU_STATES.OPEN
												? META_MENU_STATES.OPEN
												: META_MENU_STATES.OPENING,
											showInfo: false,
											stylePack: null,
											stylePackItem: '',
										});
									}}
									className="shot-builder-modal-style-pack-button button-pillbox dullgray"
								>
									<IconStylepacksDuotoneCopy color="var(--local-bttn-sec-default-icotext)" width="16" height="16"/>
									<span className="bl-font-l1 grayscale-200">All Stylepacks</span>
								</button>
								{stylepackUsed && (
									<button
										onClick={() => {
											const allTemplates = contentTemplates.filter((t) => t.stylePack === stylepackUsed.stylePack);
											const newStylePack = {
												id: stylepackUsed.stylePack,
												locked: !allTemplates.find((t) => !t.locked),
												name: stylepackUsed.name,
												stylePack: templateOrder.map((templateKey: string) => {
													const templates = allTemplates.filter((t) => t.contentType === templateKey);
													return {
														type: templateKey,
														templates,
													} as StylePackGroup;
												})
											};
											handleDrawerStateChange({
												state: shotModalStylepackDrawer.state === META_MENU_STATES.OPEN
													? META_MENU_STATES.OPEN
													: META_MENU_STATES.OPENING,
												showInfo: true,
												stylePack: newStylePack,
												stylePackItem: '',
											});
										}}
										className={`button-pillbox shot-builder-modal-style-pack-button style-pack-specific-button ${stylepackUsed.stylePack}`}
									>
										<IconTakesHighlightsDuotone width="16" height="16"/>
										<span className="bl-font-l1">{stylepackUsed.name}</span>
									</button>
								)}
							</>
						)}
					</div>
					<div className="shot-builder-modal-footer-right-buttons">
						<button
							onClick={onDiscard}
							className="button-pillbox dullgray bl-font-l1"
							disabled={upsertingContent}
						>
							Cancel
						</button>
						<TooltipWrapper message={!isLocked ? '' : 'Changes loading'}>
							<button
								onClick={handleSave}
								className={`button-pillbox sherbert${isLocked ? ' disabled' : ''} bl-font-l1`}
								disabled={isLocked}
							>
								{addUpdateButtonText}
							</button>
						</TooltipWrapper>
					</div>
				</div>
			</>}

			{modalPage === SHOT_MODAL_PAGES.SHOT_DELETED && <>
				<div className="flex shot-builder-modal-body width-full align-items-center justify-content-center">
					<span className="font-size-48 font-weight-bold">Shot Deleted</span>
				</div>
				<div className="align-items-center justify-content-space-between shot-builder-modal-footer">
					<div className="align-items-center width-full justify-content-end margin-right-large">
						<button onClick={onDiscard} className="padding-button-medium background-light corner-round-massive margin-right-medium button-background-shading">
							Close
						</button>
					</div>
				</div>
			</>}
		</div>
	);
}
